/**
 * SRS / spaced repetition (Duolingo style) — a PURE, DERIVED planner.
 *
 * It introduces no new state: the review calendar is DERIVED from the already
 * synced fields of `lesson_progress` (`attempts`, `score`, `completed_at`,
 * `updated_at`). Reviewing a lesson IS replaying it with the normal player —
 * the existing write-path increments `attempts`, re-grades `score` on the
 * server and refreshes `updated_at`, so the calendar advances on its own. Zero
 * migrations, zero new write-path, offline-first for free.
 *
 * Interval ladder (SM-2 lite): 1 → 3 → 7 → 14 → 30 → 60 days by number of
 * runs (`attempts`), moderated by the quality of the last grade:
 *   - score ≥ STRONG_SCORE  → full ladder interval.
 *   - score < STRONG_SCORE  → half interval (minimum 1 day): weak material returns sooner.
 * Only COMPLETED lessons enter (a failed lesson is still the path's
 * "current" node, not a review).
 */

/** Interval ladder in days by number of runs (attempts 1..N). */
export const SRS_INTERVAL_LADDER_DAYS = [1, 3, 7, 14, 30, 60] as const;

/** Grade at which the last run counts as solid. */
export const SRS_STRONG_SCORE = 0.85;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Deterministic order: by due date asc, stable tie-break by lessonId. */
function byDueThenId(a: ReviewItem, b: ReviewItem): number {
  if (a.dueAt !== b.dueAt) {
    return a.dueAt - b.dueAt;
  }
  if (a.lessonId < b.lessonId) {
    return -1;
  }
  return a.lessonId > b.lessonId ? 1 : 0;
}

/** Minimal progress row the planner needs (subset of lesson_progress). */
export interface SrsProgressRow {
  lesson_id: string | null;
  course_id: string | null;
  status: string | null;
  score: number | null;
  attempts: number | null;
  completed_at: string | null;
  updated_at: string | null;
}

export interface ReviewItem {
  lessonId: string;
  courseId: string;
  /** Epoch ms when the review is due. */
  dueAt: number;
  /** Days of the applied interval (for the UI). */
  intervalDays: number;
  /** true if already overdue (dueAt <= now). */
  due: boolean;
}

export interface ReviewQueue {
  /** Overdue, most overdue first. */
  due: ReviewItem[];
  /** Upcoming, soonest first. */
  upcoming: ReviewItem[];
}

/**
 * Interval (days) until the next review by runs and quality of the last
 * grade. `attempts` counts completed runs (≥1); odd values are clamped.
 */
export function computeReviewIntervalDays(attempts: number, score: number): number {
  const idx = Math.min(Math.max(Math.floor(attempts), 1), SRS_INTERVAL_LADDER_DAYS.length) - 1;
  const ladder = SRS_INTERVAL_LADDER_DAYS[idx] ?? 1;
  if (score >= SRS_STRONG_SCORE) {
    return ladder;
  }
  // Weak run: returns sooner (half interval, never less than 1 day).
  return Math.max(1, Math.round(ladder / 2));
}

/**
 * Time of the next review for a progress row, or `null` if the row does not
 * enter the review cycle (not completed or without usable dates).
 * The base is the LAST touch (`updated_at`, with `completed_at` as fallback):
 * each played review pushes the next one.
 */
export function computeNextReviewAt(row: SrsProgressRow): ReviewItem | null {
  if (row.status !== "completed" || !row.lesson_id || !row.course_id) {
    return null;
  }
  const baseIso = row.updated_at ?? row.completed_at;
  if (!baseIso) {
    return null;
  }
  const base = Date.parse(baseIso);
  if (Number.isNaN(base)) {
    return null;
  }
  const attempts = row.attempts ?? 1;
  const score = row.score ?? 0;
  const intervalDays = computeReviewIntervalDays(attempts, score);
  return {
    lessonId: row.lesson_id,
    courseId: row.course_id,
    dueAt: base + intervalDays * DAY_MS,
    intervalDays,
    due: false,
  };
}

/**
 * Review queue derived from synced progress: separates overdue from
 * upcoming and sorts deterministically (overdue: most overdue first; upcoming:
 * soonest first; ties broken by lessonId for stability).
 */
export function buildReviewQueue(rows: readonly SrsProgressRow[], now: number): ReviewQueue {
  const items: ReviewItem[] = [];
  for (const row of rows) {
    const item = computeNextReviewAt(row);
    if (item) {
      items.push({ ...item, due: item.dueAt <= now });
    }
  }
  return {
    due: items.filter((i) => i.due).toSorted(byDueThenId),
    upcoming: items.filter((i) => !i.due).toSorted(byDueThenId),
  };
}
